/**
 * Spanish A1 Vocabulary -- Frequency-Ranked
 * Based on corpus frequency data (RAE CREA, Davies' Corpus del Español)
 * Top 120 highest-frequency A1 words for Spanish
 */

import type { VocabularyItem } from '@/types/language';

export const SPANISH_A1_VOCABULARY: VocabularyItem[] = [
  {
    id: 'es-a1-001',
    word: 'el / la',
    translation: 'the',
    partOfSpeech: 'determiner',
    exampleSentences: [
      { sentence: 'El libro es nuevo.', translation: 'The book is new.' },
      { sentence: 'La casa es grande.', translation: 'The house is big.' },
    ],
    difficulty: 1,
    tags: ['articles', 'basic'],
    cefrLevel: 'A1',
    frequencyRank: 1,
    mnemonic: 'Like el in elemental.',
  },
  {
    id: 'es-a1-002',
    word: 'de',
    translation: 'of, from',
    partOfSpeech: 'preposition',
    exampleSentences: [
      { sentence: 'El libro de Maria.', translation: 'Maria book.' },
      { sentence: 'Soy de Espana.', translation: 'I am from Spain.' },
    ],
    difficulty: 1,
    tags: ['prepositions', 'basic'],
    cefrLevel: 'A1',
    frequencyRank: 2,
  },
  {
    id: 'es-a1-003',
    word: 'que',
    translation: 'that, which, who',
    partOfSpeech: 'conjunction',
    exampleSentences: [
      { sentence: 'El libro que leo.', translation: 'The book that I read.' },
    ],
    difficulty: 2,
    tags: ['conjunctions', 'basic'],
    cefrLevel: 'A1',
    frequencyRank: 3,
  },
  {
    id: 'es-a1-004',
    word: 'y',
    translation: 'and',
    partOfSpeech: 'conjunction',
    exampleSentences: [
      { sentence: 'Pan y leche.', translation: 'Bread and milk.' },
    ],
    difficulty: 1,
    tags: ['conjunctions', 'basic'],
    cefrLevel: 'A1',
    frequencyRank: 4,
  },
  {
    id: 'es-a1-005',
    word: 'en',
    translation: 'in, on, at',
    partOfSpeech: 'preposition',
    exampleSentences: [
      { sentence: 'Estoy en casa.', translation: 'I am at home.' },
    ],
    difficulty: 2,
    tags: ['prepositions', 'basic'],
    cefrLevel: 'A1',
    frequencyRank: 5,
  },
  {
    id: 'es-a1-006',
    word: 'ser',
    translation: 'to be (permanent)',
    partOfSpeech: 'verb',
    exampleSentences: [
      { sentence: 'Soy estudiante.', translation: 'I am a student.' },
      { sentence: 'Eres alto.', translation: 'You are tall.' },
    ],
    difficulty: 2,
    tags: ['verbs', 'essential'],
    cefrLevel: 'A1',
    frequencyRank: 6,
    relatedWordIds: ['es-a1-007'],
  },
  {
    id: 'es-a1-007',
    word: 'estar',
    translation: 'to be (temporary/location)',
    partOfSpeech: 'verb',
    exampleSentences: [
      { sentence: 'Estoy cansado.', translation: 'I am tired.' },
      { sentence: 'El libro esta aqui.', translation: 'The book is here.' },
    ],
    difficulty: 2,
    tags: ['verbs', 'essential'],
    cefrLevel: 'A1',
    frequencyRank: 7,
    mnemonic: 'Estar = state (temporary).',
    relatedWordIds: ['es-a1-006'],
  },
  {
    id: 'es-a1-008',
    word: 'no',
    translation: 'no, not',
    partOfSpeech: 'adverb',
    exampleSentences: [
      { sentence: 'No, gracias.', translation: 'No, thank you.' },
      { sentence: 'No se.', translation: 'I do not know.' },
    ],
    difficulty: 1,
    tags: ['basic', 'negation'],
    cefrLevel: 'A1',
    frequencyRank: 8,
  },
  {
    id: 'es-a1-009',
    word: 'a',
    translation: 'to, at',
    partOfSpeech: 'preposition',
    exampleSentences: [
      { sentence: 'Voy a la escuela.', translation: 'I go to school.' },
    ],
    difficulty: 2,
    tags: ['prepositions', 'basic'],
    cefrLevel: 'A1',
    frequencyRank: 9,
  },
  {
    id: 'es-a1-010',
    word: 'un / una',
    translation: 'a, an, one',
    partOfSpeech: 'determiner',
    exampleSentences: [
      { sentence: 'Un perro.', translation: 'A dog.' },
      { sentence: 'Una manzana.', translation: 'An apple.' },
    ],
    difficulty: 1,
    tags: ['articles', 'basic'],
    cefrLevel: 'A1',
    frequencyRank: 10,
  },
  {
    id: 'es-a1-011',
    word: 'tener',
    translation: 'to have',
    partOfSpeech: 'verb',
    exampleSentences: [
      { sentence: 'Tengo un libro.', translation: 'I have a book.' },
    ],
    difficulty: 2,
    tags: ['verbs', 'essential'],
    cefrLevel: 'A1',
    frequencyRank: 11,
  },
  {
    id: 'es-a1-012',
    word: 'me',
    translation: 'me, myself',
    partOfSpeech: 'pronoun',
    exampleSentences: [
      { sentence: 'Me llamo Juan.', translation: 'My name is Juan.' },
      { sentence: 'Me gusta el cafe.', translation: 'I like coffee.' },
    ],
    difficulty: 2,
    tags: ['pronouns', 'reflexive'],
    cefrLevel: 'A1',
    frequencyRank: 12,
  },
  {
    id: 'es-a1-013',
    word: 'se',
    translation: 'himself, herself, itself',
    partOfSpeech: 'pronoun',
    exampleSentences: [
      { sentence: 'Se llama Ana.', translation: 'Her name is Ana.' },
    ],
    difficulty: 3,
    tags: ['pronouns', 'reflexive'],
    cefrLevel: 'A1',
    frequencyRank: 13,
  },
  {
    id: 'es-a1-014',
    word: 'con',
    translation: 'with',
    partOfSpeech: 'preposition',
    exampleSentences: [
      { sentence: 'Con leche.', translation: 'With milk.' },
      { sentence: 'Voy con mi amigo.', translation: 'I go with my friend.' },
    ],
    difficulty: 1,
    tags: ['prepositions', 'basic'],
    cefrLevel: 'A1',
    frequencyRank: 14,
  },
];

export default SPANISH_A1_VOCABULARY;
